import { api } from '../client';
import { uploadFileDirectToS3 } from './uploads';
import type {
  WhatsappAlert,
  ApprovalLimits,
  MyApprovalRow,
  ChangeRequestStatus,
  ProfileChangeRequest,
  ProfileChangeRequestPage,
  AdminKycCheck,
  AdminVerificationChecksResponse,
  KycCheckType,
  DelayPreviewResponse,
  DelayReason,
  RecordDelayRequest,
  ShipperMessage,
  ShipperBillingConditions,
  ShipperBillingConditionsRequest,
  RateCardResponse,
  RateQuoteQuery,
  RateQuoteResponse,
  PriceSource,
  AdminAnalyticsOverviewResponse,
  AdminDashboardResponse,
  AdminDocumentResponse,
  DriverReadiness,
  EditStopRequest,
  OpenStopRequest,
  StopResponse,
  AdminUserResponse,
  AdminUserDetailResponse,
  AdminAccountResponse,
  AdminAccountDetailResponse,
  VendorImportResponse,
  ShipmentResponse,
  PagedResponse,
  ShipmentStatus,
  DocumentStatus,
  KycStatus,
  UserRole,
  DocumentResponse,
  TrackingEventResponse,
  AdminUserOverviewResponse,
  AdminVehicleResponse,
  AdminRatingResponse,
  MarginConfigResponse,
  ServiceLaneSearchResponse,
  ServiceLaneResponse,
  DriverProfileResponse,
  ShipperProfileResponse,
  FleetManagerProfileResponse,
  FleetManagerImportRecord,
  ShipperImportRecord,
  ShipmentVendorMatchResponse,
  FleetSuggestionResponse,
  NotificationRunResponse,
  NotificationDetailResponse,
  MatchingPolicyResponse,
  MatchingPolicyDraftRequest,
  NotificationPolicyResponse,
  NotificationPolicyDraftRequest,
  AdminInterestSummaryResponse,
  AdminInterestDetailResponse,
  AdminInterestActionRequest,
  AdminInterestConvertToBidRequest,
  AdminInterestQueueParams,
  HistoryFilter,
  NotificationHistoryPage,
  NotificationSummaryResponse,
  NotificationTimelineResponse,
  BidStatus,
  ReverseAwardReasonCode,
  ReverseAwardPayload,
} from '../types';

export interface AdminPingResponse {
  status: string;
  user: string;
  serverTime: string;
}

/** Phase 0 connectivity check — proves the full auth chain end-to-end. */
export async function adminPing(): Promise<AdminPingResponse> {
  const { data } = await api.get<AdminPingResponse>('/admin/ping');
  return data;
}

// ─── Margin config ────────────────────────────────────────────────────────────

export async function getMarginConfig(): Promise<MarginConfigResponse> {
  const { data } = await api.get<MarginConfigResponse>('/admin/margin-config');
  return data;
}

export interface UpdateMarginConfigPayload {
  carrierMarginPct: number;
  shipperMarginPct: number;
}

export async function updateMarginConfig(
  payload: UpdateMarginConfigPayload,
): Promise<MarginConfigResponse> {
  const { data } = await api.put<MarginConfigResponse>('/admin/margin-config', payload);
  return data;
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export async function getDashboard(): Promise<AdminDashboardResponse> {
  const { data } = await api.get<AdminDashboardResponse>('/admin/dashboard');
  return data;
}

// ─── Analytics ────────────────────────────────────────────────────────────────

export interface AnalyticsOverviewParams {
  /** ISO-8601 instant, e.g. new Date(...).toISOString() */
  from: string;
  to: string;
}

export async function getAnalyticsOverview(
  params: AnalyticsOverviewParams,
): Promise<AdminAnalyticsOverviewResponse> {
  const { data } = await api.get<AdminAnalyticsOverviewResponse>('/admin/analytics/overview', {
    params,
  });
  return data;
}

// ─── Shipments ────────────────────────────────────────────────────────────────

export interface ShipmentListParams {
  page?: number;
  size?: number;
  sort?: string;
  search?: string;
  status?: ShipmentStatus | 'ACTIVE' | 'ORDERS';
  /** Ops-owner filter: `me`, `unassigned` or an ops user id. */
  owner?: string;
  /** true = only shipments with an open stop, oldest stop first (dnm-work#165 C5). */
  stopped?: boolean;
}

export async function getShipments(
  params: ShipmentListParams = {},
): Promise<PagedResponse<ShipmentResponse>> {
  const { data } = await api.get<PagedResponse<ShipmentResponse>>('/admin/shipments', { params });
  return data;
}

// ─── Documents ────────────────────────────────────────────────────────────────

export interface DocumentListParams {
  page?: number;
  size?: number;
  status?: DocumentStatus;
}

export async function getDocuments(
  params: DocumentListParams = {},
): Promise<PagedResponse<AdminDocumentResponse>> {
  const { data } = await api.get<PagedResponse<AdminDocumentResponse>>('/admin/documents', {
    params,
  });
  return data;
}

// ─── Users ────────────────────────────────────────────────────────────────────

export interface UserListParams {
  page?: number;
  size?: number;
  search?: string;
  role?: UserRole;
  kycStatus?: KycStatus;
}

export async function getUsers(
  params: UserListParams = {},
): Promise<PagedResponse<AdminUserResponse>> {
  const { data } = await api.get<PagedResponse<AdminUserResponse>>('/admin/users', { params });
  return data;
}

export async function getUserById(id: number): Promise<AdminUserDetailResponse> {
  const { data } = await api.get<AdminUserDetailResponse>(`/admin/users/${id}`);
  return data;
}

export async function getShipmentById(id: number): Promise<ShipmentResponse> {
  const { data } = await api.get<ShipmentResponse>(`/shipments/${id}`);
  return data;
}

export interface BidResponse {
  id: number;
  shipmentId: number;
  bidderId: number;
  bidderName: string | null;
  bidderPhone: string | null;
  /** ADMIN-only: the bidder's fleet company (trimmed, blank → null). */
  bidderCompanyName?: string | null;
  amount: number;
  /** ADMIN-only: the per-bid shipper-facing figure (amount + margin snapshot). */
  shipperFacingAmount: number | null;
  shipperMarginPct: number | null;
  /** ADMIN-only (#221): true when staff typed a fixed shipper price; older API → absent. */
  shipperAmountFixed?: boolean | null;
  /** ADMIN-only (#221): server margin in rupees (shipperFacingAmount − amount). */
  marginAmount?: number | null;
  /** ADMIN-only (#221): server margin % over the carrier amount, 2 dp. */
  effectiveMarginPct?: number | null;
  /** ADMIN-only (#221): true when the shipper pays less than the carrier gets. */
  negativeMargin?: boolean | null;
  etaHours: number;
  notes: string | null;
  /** AWARD_REVERSED arrives on admin endpoints only (rhino-api#277); the fallback tolerates POSTED and future values. */
  status: BidStatus | (string & NonNullable<unknown>);
  shipmentStatus: string;
  createdAt: string;
  postingCycle?: number | null;
  proposedPickupTime?: string | null;
  /** ADMIN-only: reason code for an AWARD_REVERSED bid (rhino-ops-console#230 Contract). */
  reversalReasonCode?: ReverseAwardReasonCode | (string & NonNullable<unknown>) | null;
  /** ADMIN-only: optional note for an AWARD_REVERSED bid (rhino-ops-console#230 Contract). */
  reversalNote?: string | null;
  /** ADMIN and the shipment's shipper: false = awarding this bid is blocked by the bidder's KYC; null = unknown. */
  bidderKycVerified?: boolean | null;
}

export async function getBidsForShipment(shipmentId: number): Promise<BidResponse[]> {
  const { data } = await api.get<BidResponse[]>(`/shipments/${shipmentId}/bids`);
  return data;
}

export async function getLoadByUuid(shipmentUuid: string): Promise<ShipmentResponse> {
  const { data } = await api.get<ShipmentResponse>(`/loads/${shipmentUuid}`);
  return data;
}

export async function getBidsForLoad(shipmentUuid: string): Promise<BidResponse[]> {
  const { data } = await api.get<BidResponse[]>(`/loads/${shipmentUuid}/bids`);
  return data;
}

export async function getDocumentsForShipment(shipmentId: number): Promise<DocumentResponse[]> {
  const { data } = await api.get<DocumentResponse[]>(`/shipments/${shipmentId}/documents`);
  return data;
}

export async function getTrackingHistory(shipmentId: number): Promise<TrackingEventResponse[]> {
  const { data } = await api.get<TrackingEventResponse[]>(`/shipments/${shipmentId}/tracking`);
  return data;
}

// ─── Phase 3: user create/edit ────────────────────────────────────────────────

export interface CreateUserPayload {
  role: Exclude<UserRole, 'ADMIN'>;
  phoneNumber: string;
  fullName: string;
  email?: string;
  companyName?: string;
  gstin?: string;
  serviceRegions?: string;
  fleetSize?: number;
  secondaryPhoneNumber?: string;
  whatsappNumber?: string;
  billingAddress?: string;
}

export async function createUser(payload: CreateUserPayload): Promise<AdminUserResponse> {
  const { data } = await api.post<AdminUserResponse>('/admin/users', payload);
  return data;
}

const VENDOR_IMPORT_CHUNK_SIZE = 25;

/**
 * Imports rows `[offset, offset + limit)` of the file. The backend
 * re-validates the WHOLE file all-or-nothing on every call, so a failure
 * here can mean either "the file has a bad row somewhere" (first call) or
 * "a row failed mid-batch" (later call) — both come back as a normal 422
 * body, not a request failure, so 422 is accepted alongside 201 rather than
 * treated as an error by the shared axios interceptor.
 */
async function importFleetManagerVendorsChunk(
  file: File,
  offset: number,
  limit: number,
): Promise<VendorImportResponse> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<VendorImportResponse>(
    `/admin/users/fleet-managers/import?offset=${offset}&limit=${limit}`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      validateStatus: (status) => status === 201 || status === 422,
    },
  );
  return data;
}

/**
 * Uploads the vendor CSV in small chunks instead of one long request — each
 * chunk only creates a bounded number of rows, so every individual request
 * stays fast regardless of total file size (no special client/ALB timeout
 * needed). All-or-nothing: if any chunk reports failures, nothing further
 * is created and the loop stops immediately.
 *
 * @param onProgress called after each successful chunk with the running
 *   imported count and total row count, so the caller can show progress.
 */
export async function importFleetManagerVendors(
  file: File,
  onProgress?: (importedSoFar: number, totalRows: number) => void,
): Promise<VendorImportResponse> {
  let offset = 0;
  let imported: VendorImportResponse['imported'] = [];
  let totalRows = 0;

  for (;;) {
    const chunk = await importFleetManagerVendorsChunk(file, offset, VENDOR_IMPORT_CHUNK_SIZE);
    totalRows = chunk.totalRows;

    if (chunk.failures.length > 0) {
      return { imported, failures: chunk.failures, totalRows, nextOffset: null };
    }

    imported = imported.concat(chunk.imported);
    onProgress?.(imported.length, totalRows);

    if (chunk.nextOffset == null) break;
    offset = chunk.nextOffset;
  }

  return { imported, failures: [], totalRows, nextOffset: null };
}

async function bulkImportFleetManagersChunk(
  records: FleetManagerImportRecord[],
  offset: number,
  limit: number,
  overrideExisting: boolean = false,
): Promise<VendorImportResponse> {
  const { data } = await api.post<VendorImportResponse>(
    '/admin/users/fleet-managers/bulk-import',
    { records, offset, limit, overrideExisting },
    {
      validateStatus: (status) => status === 201 || status === 422,
    },
  );
  return data;
}

/**
 * Bulk-imports mapped fleet manager records in small chunks with progress tracking.
 */
export async function bulkImportFleetManagers(
  records: FleetManagerImportRecord[],
  onProgress?: (importedSoFar: number, totalRows: number) => void,
  overrideExisting: boolean = false,
): Promise<VendorImportResponse> {
  let offset = 0;
  let imported: VendorImportResponse['imported'] = [];
  const totalRows = records.length;

  for (;;) {
    const chunk = await bulkImportFleetManagersChunk(records, offset, VENDOR_IMPORT_CHUNK_SIZE, overrideExisting);

    if (chunk.failures.length > 0) {
      return { imported, failures: chunk.failures, totalRows, nextOffset: null };
    }

    imported = imported.concat(chunk.imported);
    onProgress?.(imported.length, totalRows);

    if (chunk.nextOffset == null) break;
    offset = chunk.nextOffset;
  }

  return { imported, failures: [], totalRows, nextOffset: null };
}

async function importShippersChunk(
  file: File,
  offset: number,
  limit: number,
): Promise<VendorImportResponse> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<VendorImportResponse>(
    `/admin/users/shippers/import?offset=${offset}&limit=${limit}`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      validateStatus: (status) => status === 201 || status === 422,
    },
  );
  return data;
}

export async function importShippers(
  file: File,
  onProgress?: (importedSoFar: number, totalRows: number) => void,
): Promise<VendorImportResponse> {
  let offset = 0;
  let imported: VendorImportResponse['imported'] = [];
  let totalRows = 0;

  for (;;) {
    const chunk = await importShippersChunk(file, offset, VENDOR_IMPORT_CHUNK_SIZE);
    totalRows = chunk.totalRows;

    if (chunk.failures.length > 0) {
      return { imported, failures: chunk.failures, totalRows, nextOffset: null };
    }

    imported = imported.concat(chunk.imported);
    onProgress?.(imported.length, totalRows);

    if (chunk.nextOffset == null) break;
    offset = chunk.nextOffset;
  }

  return { imported, failures: [], totalRows, nextOffset: null };
}

async function bulkImportShippersChunk(
  records: ShipperImportRecord[],
  offset: number,
  limit: number,
  overrideExisting: boolean = false,
): Promise<VendorImportResponse> {
  const { data } = await api.post<VendorImportResponse>(
    '/admin/users/shippers/bulk-import',
    { records, offset, limit, overrideExisting },
    {
      validateStatus: (status) => status === 201 || status === 422,
    },
  );
  return data;
}

/**
 * Bulk-imports mapped shipper records in small chunks with progress tracking.
 */
export async function bulkImportShippers(
  records: ShipperImportRecord[],
  onProgress?: (importedSoFar: number, totalRows: number) => void,
  overrideExisting: boolean = false,
): Promise<VendorImportResponse> {
  let offset = 0;
  let imported: VendorImportResponse['imported'] = [];
  const totalRows = records.length;

  for (;;) {
    const chunk = await bulkImportShippersChunk(records, offset, VENDOR_IMPORT_CHUNK_SIZE, overrideExisting);

    if (chunk.failures.length > 0) {
      return { imported, failures: chunk.failures, totalRows, nextOffset: null };
    }

    imported = imported.concat(chunk.imported);
    onProgress?.(imported.length, totalRows);

    if (chunk.nextOffset == null) break;
    offset = chunk.nextOffset;
  }

  return { imported, failures: [], totalRows, nextOffset: null };
}

export interface UpdateUserPayload {
  fullName?: string;
  email?: string;
  companyName?: string;
  gstin?: string;
  serviceRegions?: string;
  fleetSize?: number;
  secondaryPhoneNumber?: string;
  whatsappNumber?: string;
  billingAddress?: string;
}

export async function updateUser(
  id: number,
  payload: UpdateUserPayload,
): Promise<AdminUserResponse> {
  const { data } = await api.patch<AdminUserResponse>(`/admin/users/${id}`, payload);
  return data;
}

export interface UpsertFleetManagerProfilePayload {
  companyName?: string;
  gstin?: string;
  serviceRegions?: string;
  fleetSize?: number;
  secondaryPhoneNumber?: string;
  whatsappNumber?: string;
  billingAddress?: string;
  bankName?: string;
  accountHolderName?: string;
  accountNumber?: string;
  ifscCode?: string;
  bankProofType?: string;
  bankProofUrl?: string;
  panNumber?: string;
  panDocumentUrl?: string;
  gstDocumentUrl?: string;
}

export async function adminUpdateFleetManagerProfile(
  fleetManagerId: number,
  payload: UpsertFleetManagerProfilePayload,
): Promise<FleetManagerProfileResponse> {
  const { data } = await api.put<FleetManagerProfileResponse>(
    `/admin/users/fleet-managers/${fleetManagerId}/profile`,
    payload,
  );
  return data;
}

export interface UpsertDriverProfilePayload {
  vehicleType: string;
  vehicleNumber?: string;
  vehicleCapacityKg?: number;
  licenseNumber?: string;
  licenseExpiry?: string;
  dlDocumentUrl?: string;
  rcDocumentUrl?: string;
}

export async function adminUpdateDriverProfile(
  driverId: number,
  payload: UpsertDriverProfilePayload,
): Promise<DriverProfileResponse> {
  const { data } = await api.put<DriverProfileResponse>(
    `/admin/users/drivers/${driverId}/profile`,
    payload,
  );
  return data;
}

export interface UpsertShipperProfilePayload {
  companyName?: string;
  gstin?: string;
  panNumber?: string;
  panDocumentUrl?: string;
  gstDocumentUrl?: string;
  secondaryPhoneNumber?: string;
  whatsappNumber?: string;
  billingAddressText?: string;
}

export async function adminUpdateShipperProfile(
  shipperId: number,
  payload: UpsertShipperProfilePayload,
): Promise<ShipperProfileResponse> {
  const { data } = await api.put<ShipperProfileResponse>(
    `/admin/users/shippers/${shipperId}/profile`,
    payload,
  );
  return data;
}

// ─── Phase 3: shipment create/edit + bids + tracking + documents ─────────────

export interface ShipmentPayload {
  originAddress: string;
  originCity: string;
  destinationAddress: string;
  destinationCity: string;
  cargoDescription: string;
  weightKg?: number;
  /** Standardized truck-catalog id (e.g. "container_32_mxl") — see truckCatalog.ts */
  vehicleTypeRequired?: string;
  /** Typed "Other" truck (2–40 chars, #249); `""` clears it on edit. Never sent non-blank with `vehicleTypeRequired`. */
  vehicleTypeOther?: string;
  volumeCbm?: number;
  pickupDate: string; // yyyy-MM-dd
  deliveryDate?: string;
  offeredPrice?: number;
  /** #298 (demo only until the build issue): where the offered price came from. */
  priceSource?: PriceSource;
  /** #298: 3–500 chars, required when MANUAL and a quote existed; null otherwise. */
  priceOverrideReason?: string | null;
  // Optional Google Places geocoded fields (set when the admin picks an
  // autocomplete suggestion) — mirrors rhino-api's CreateShipmentRequest.
  originDistrict?: string;
  originState?: string;
  originPincode?: string;
  originLat?: number;
  originLng?: number;
  originPlaceId?: string;
  destinationDistrict?: string;
  destinationState?: string;
  destinationPincode?: string;
  destinationLat?: number;
  destinationLng?: number;
  destinationPlaceId?: string;
  distanceKm?: number | null;
  estimatedDurationMinutes?: number | null;
  distanceIsEstimated?: boolean | null;
}

export async function adminCreateShipment(
  shipperId: number,
  shipment: ShipmentPayload,
): Promise<ShipmentResponse> {
  const { data } = await api.post<ShipmentResponse>('/admin/shipments', { shipperId, shipment });
  return data;
}

export async function adminUpdateShipment(
  id: number,
  payload: Partial<ShipmentPayload>,
): Promise<ShipmentResponse> {
  const { data } = await api.patch<ShipmentResponse>(`/admin/shipments/${id}`, payload);
  return data;
}

/** Asks the server to recompute the stored road distance (DRAFT/POSTED only). */
export async function recomputeShipmentDistance(id: number): Promise<ShipmentResponse> {
  const { data } = await api.post<ShipmentResponse>(`/admin/shipments/${id}/distance/recompute`);
  return data;
}

/** Request body for POST /admin/shipments/{id}/address (dnm-work#108 Contract). */
export interface ShipmentAddressEditPayload {
  stop: 'PICKUP' | 'DROP';
  /** ≤500 */
  address: string;
  /** ≤100 */
  city: string;
  district?: string;
  state?: string;
  /** ≤10 */
  pincode?: string;
  latitude: number;
  longitude: number;
  /** ≤255 */
  placeId?: string;
  /** Trimmed, 3–300 */
  reason: string;
}

/** Changes one stop's address after a driver is assigned; the price is not touched. */
export async function adminEditShipmentAddress(
  id: number,
  body: ShipmentAddressEditPayload,
): Promise<ShipmentResponse> {
  const { data } = await api.post<ShipmentResponse>(`/admin/shipments/${id}/address`, body);
  return data;
}

/** The shipper-pricing part of a bid request: `shipperAmount` XOR `shipperMarginPct`, or neither (R10). */
export type ShipperPricing = { shipperAmount: number } | { shipperMarginPct: number } | undefined;

export interface BidPayload {
  price: number;
  etaHours: number;
  notes?: string;
  proposedPickupTime?: string;
}

export async function adminPlaceBid(
  shipmentId: number,
  bidderId: number,
  bid: BidPayload,
  /** Fixed shipper price XOR per-bid margin (R10); omitted -> current default config. */
  shipperPricing?: ShipperPricing,
): Promise<BidResponse> {
  const { data } = await api.post<BidResponse>(`/admin/shipments/${shipmentId}/bids`, {
    bidderId,
    bid,
    ...(shipperPricing ?? {}),
  });
  return data;
}

/** A PATCH with only a price keeps a Fixed shipper price; a pct clears it (#221 Contract). */
export async function adminUpdateBid(
  bidId: number,
  payload: Partial<BidPayload>,
  shipperPricing?: ShipperPricing,
): Promise<BidResponse> {
  const { data } = await api.patch<BidResponse>(`/admin/bids/${bidId}`, {
    ...payload,
    ...(shipperPricing ?? {}),
  });
  return data;
}

export interface UpdateShipperFreightPayload {
  /** Whole rupees, ≥ 1. */
  shipperFreight: number;
  /** Trimmed, 1–500 characters. */
  reason: string;
}

/**
 * Edits the shipper final after award, until the shipper invoice is issued (#221, R10 step B).
 * 409 SHIPPER_INVOICE_ISSUED / NOT_AWARDED; 400 validation.
 */
export async function updateShipperFreight(
  shipmentId: number,
  payload: UpdateShipperFreightPayload,
): Promise<ShipmentResponse> {
  const { data } = await api.patch<ShipmentResponse>(
    `/admin/shipments/${shipmentId}/shipper-freight`,
    payload,
  );
  return data;
}

export interface UpdatePartnerOfferPricePayload {
  /** Whole rupees, ≥ 1; null clears the price (partners then see no price). */
  partnerOfferPrice: number | null;
  /** Trimmed, 1–500 characters. */
  reason: string;
}

/**
 * Sets or clears the per-order partner offer price (#288, Contract dnm-work#30, rhino-api R36).
 * DRAFT / POSTED only: 409 NOT_OPEN_FOR_BIDS otherwise; 400 validation; the same value is a 200 no-op.
 */
export async function updatePartnerOfferPrice(
  shipmentId: number,
  payload: UpdatePartnerOfferPricePayload,
): Promise<ShipmentResponse> {
  const { data } = await api.patch<ShipmentResponse>(
    `/admin/shipments/${shipmentId}/partner-offer-price`,
    payload,
  );
  return data;
}

export type TrackingMilestone =
  'EN_ROUTE_PICKUP' | 'AT_PICKUP' | 'LOADED' | 'IN_TRANSIT' | 'AT_DROP' | 'DELIVERED';

export interface AdminUpdateStatusPayload {
  status: ShipmentStatus;
  reason?: string;
  latitude?: number;
  longitude?: number;
}

export async function adminUpdateShipmentStatus(
  shipmentId: number,
  payload: AdminUpdateStatusPayload,
): Promise<ShipmentResponse> {
  const { data } = await api.post<ShipmentResponse>(
    `/admin/shipments/${shipmentId}/status`,
    payload,
  );
  return data;
}

export interface AdminTrackingEventPayload {
  type?: 'LOCATION_PING' | 'MILESTONE';
  milestone?: TrackingMilestone;
  latitude?: number;
  longitude?: number;
  locationName?: string;
}

export async function adminAddTrackingEvent(
  shipmentId: number,
  milestoneOrPayload: TrackingMilestone | AdminTrackingEventPayload,
  latitude?: number,
  longitude?: number,
  locationName?: string,
): Promise<TrackingEventResponse> {
  const body: AdminTrackingEventPayload =
    typeof milestoneOrPayload === 'string'
      ? { milestone: milestoneOrPayload, latitude, longitude, locationName, type: 'MILESTONE' }
      : milestoneOrPayload;
  const { data } = await api.post<TrackingEventResponse>(
    `/admin/shipments/${shipmentId}/tracking-events`,
    body,
  );
  return data;
}

export type UploadDocumentType =
  | 'LR_EWAY'
  | 'LOADING_PHOTO'
  | 'ID_PROOF'
  | 'POD'
  | 'OTHER'
  | 'VEHICLE_RC'
  | 'VEHICLE_PHOTO';

export async function uploadShipmentDocument(
  shipmentId: number,
  type: UploadDocumentType,
  file: File,
): Promise<DocumentResponse> {
  const result = await uploadFileDirectToS3({
    file,
    purpose: 'SHIPMENT_DOCUMENT',
    targetId: shipmentId,
    documentTag: type,
  });

  return {
    id: result.attachmentId ?? 0,
    shipmentId,
    uploadedById: 0,
    type,
    fileUrl: result.fileUrl,
    downloadUrl: result.downloadUrl,
    status: 'UPLOADED',
    verifiedById: null,
    verifiedAt: null,
    uploadedAt: result.completedAt,
    rejectionReason: null,
  };
}

// ─── Phase 2 Mutations ────────────────────────────────────────────────────────

export async function updateUserKyc(
  userId: number,
  status: 'VERIFIED' | 'REJECTED',
  reason?: string,
): Promise<AdminUserResponse> {
  const { data } = await api.post<AdminUserResponse>(`/admin/users/${userId}/kyc`, {
    status,
    reason,
  });
  return data;
}

/** Automatic PAN / bank check results for ops (Contract C6, rhino-ops-console#304). */
export async function getVerificationChecks(
  userId: number,
): Promise<AdminVerificationChecksResponse> {
  const { data } = await api.get<AdminVerificationChecksResponse>(
    `/admin/users/${userId}/verification-checks`,
  );
  return data;
}

/** Marks a MISMATCH / INVALID / ERROR / NOT_CHECKED check verified; the reason (3–500) is audited. */
export async function overrideVerificationCheck(
  userId: number,
  checkType: KycCheckType,
  reason: string,
): Promise<AdminKycCheck> {
  const { data } = await api.post<AdminKycCheck>(
    `/admin/users/${userId}/verification-checks/${checkType}/override`,
    { reason },
  );
  return data;
}

// ─── Profile change requests (#281, Contract dnm-work#142 endpoints 4–7) ─────

/** All users' requests; PENDING comes oldest first. `size` is capped at 100 by the API. */
export async function getChangeRequests(params: {
  status?: ChangeRequestStatus;
  page?: number;
  size?: number;
}): Promise<ProfileChangeRequestPage> {
  const { data } = await api.get<ProfileChangeRequestPage>('/admin/change-requests', { params });
  return data;
}

/** One user's requests, newest first. */
export async function getUserChangeRequests(userId: number): Promise<ProfileChangeRequest[]> {
  const { data } = await api.get<ProfileChangeRequest[]>(`/admin/users/${userId}/change-requests`);
  return data;
}

/** Applies only the requested fields; KYC status is unchanged (R25). */
export async function approveChangeRequest(id: number): Promise<ProfileChangeRequest> {
  const { data } = await api.post<ProfileChangeRequest>(`/admin/change-requests/${id}/approve`);
  return data;
}

/** The reason (1–200, R26) goes in the body so free text never lands in ALB logs. */
export async function rejectChangeRequest(
  id: number,
  reason: string,
): Promise<ProfileChangeRequest> {
  const { data } = await api.post<ProfileChangeRequest>(`/admin/change-requests/${id}/reject`, {
    reason,
  });
  return data;
}

export async function verifyDocument(
  documentId: number,
  approve: boolean,
  reason?: string,
): Promise<DocumentResponse> {
  // reason goes in the body (not query) so free text never lands in ALB logs
  const { data } = await api.post<DocumentResponse>(
    `/documents/${documentId}/verify`,
    reason ? { reason } : {},
    { params: { approve } },
  );
  return data;
}

export interface DriverLookupResponse {
  exists: boolean;
  driverId?: number;
  fullName?: string;
  recentVehicleNumber?: string;
}

export async function lookupDriverByPhone(phone: string): Promise<DriverLookupResponse> {
  const { data } = await api.get<DriverLookupResponse>('/drivers/lookup', {
    params: { phone },
  });
  return data;
}

export interface AdminAssignDriverPayload {
  driverId?: number;
  driverPhone?: string;
  driverName?: string;
  vehicleRegistrationNumber?: string;
  vehicleId?: number;
}

export interface AdminAssignDriverResponse {
  assignmentId: number;
  shipmentId: number;
  driverId: number;
  status: string;
  assignedAt: string;
  isNewDriver: boolean;
  /** Coded driver invite link for a newly created driver; null for a registered driver. */
  inviteUrl: string | null;
  /** Driver KYC / RC / photo readiness (warn only, R8); always sent by rhino-api#294. */
  driverReadiness?: DriverReadiness | null;
}

export async function adminAssignDriver(
  shipmentId: number,
  payload: AdminAssignDriverPayload,
): Promise<AdminAssignDriverResponse> {
  const { data } = await api.post<AdminAssignDriverResponse>(
    `/admin/shipments/${shipmentId}/assign`,
    payload,
  );
  return data;
}

export async function acceptBid(bidId: number): Promise<BidResponse> {
  const { data } = await api.post<BidResponse>(`/bids/${bidId}/accept`);
  return data;
}

export async function cancelShipment(shipmentId: number): Promise<void> {
  await api.post(`/shipments/${shipmentId}/cancel`);
}

export async function completeShipment(shipmentId: number): Promise<void> {
  await api.post(`/shipments/${shipmentId}/complete`);
}

// ─── Stops (dnm-work#165 C2; ADMIN only) ──────────────────────────────────────

/** Truck stopped: opens a stop. 409 STOP_ALREADY_OPEN / STOP_NOT_ALLOWED, 400 VALIDATION_FAILED with field. */
export async function openStop(shipmentId: number, payload: OpenStopRequest): Promise<StopResponse> {
  const { data } = await api.post<StopResponse>(`/shipments/${shipmentId}/stops`, payload);
  return data;
}

/** Edit stop: only the changed fields. 409 STOP_CLOSED, 404 STOP_NOT_FOUND. */
export async function editStop(
  shipmentId: number,
  stopId: number,
  payload: EditStopRequest,
): Promise<StopResponse> {
  const { data } = await api.patch<StopResponse>(`/shipments/${shipmentId}/stops/${stopId}`, payload);
  return data;
}

/** Moving again: closes the stop; the shipment status is not touched (C2, R53). */
export async function closeStop(shipmentId: number, stopId: number): Promise<StopResponse> {
  const { data } = await api.post<StopResponse>(`/shipments/${shipmentId}/stops/${stopId}/close`);
  return data;
}

/** Stop history, newest first. */
export async function getStops(shipmentId: number): Promise<StopResponse[]> {
  const { data } = await api.get<StopResponse[]>(`/shipments/${shipmentId}/stops`);
  return data;
}

/** Detention free time per location, whole hours 0–168 (rhino-api#378 C1). Read: any ops user. */
export async function getDetentionFreeHours(): Promise<number> {
  const { data } = await api.get<{ value: number }>('/admin/settings/detention-free-hours');
  return data.value;
}

/** (M) 0..168. 400 VALIDATION_FAILED field value. */
export async function updateDetentionFreeHours(value: number): Promise<number> {
  const { data } = await api.put<{ value: number }>('/admin/settings/detention-free-hours', { value });
  return data.value;
}

export async function repostShipment(shipmentId: number): Promise<void> {
  await api.post(`/shipments/${shipmentId}/repost`);
}

export async function reverseAward(
  shipmentId: number,
  payload: ReverseAwardPayload,
): Promise<void> {
  await api.post(`/shipments/${shipmentId}/reverse-award`, payload);
}

// ─── Persona 360 Profile Overview & Tabs (Phase 3.5 Chunk A/B) ──────────────────────────

export async function getUserOverview(userId: number): Promise<AdminUserOverviewResponse> {
  const { data } = await api.get<AdminUserOverviewResponse>(`/admin/users/${userId}/overview`);
  return data;
}

export interface UserOverviewShipmentsParams {
  page?: number;
  size?: number;
}

export async function getUserOverviewShipments(
  userId: number,
  params: UserOverviewShipmentsParams = {},
): Promise<PagedResponse<ShipmentResponse>> {
  const { data } = await api.get<PagedResponse<ShipmentResponse>>(
    `/admin/users/${userId}/shipments`,
    { params },
  );
  return data;
}

export interface UserOverviewBidsParams {
  page?: number;
  size?: number;
}

export async function getUserOverviewBids(
  userId: number,
  params: UserOverviewBidsParams = {},
): Promise<PagedResponse<BidResponse>> {
  const { data } = await api.get<PagedResponse<BidResponse>>(`/admin/users/${userId}/bids`, {
    params,
  });
  return data;
}

export async function getUserOverviewVehicles(userId: number): Promise<AdminVehicleResponse[]> {
  const { data } = await api.get<AdminVehicleResponse[]>(`/admin/users/${userId}/vehicles`);
  return data;
}

export interface UserOverviewRatingsParams {
  page?: number;
  size?: number;
}

export async function getUserOverviewRatings(
  userId: number,
  params: UserOverviewRatingsParams = {},
): Promise<PagedResponse<AdminRatingResponse>> {
  const { data } = await api.get<PagedResponse<AdminRatingResponse>>(
    `/admin/users/${userId}/ratings`,
    { params },
  );
  return data;
}

export interface UserOverviewDocumentsParams {
  page?: number;
  size?: number;
}

export async function getUserOverviewDocuments(
  userId: number,
  params: UserOverviewDocumentsParams = {},
): Promise<PagedResponse<AdminDocumentResponse>> {
  const { data } = await api.get<PagedResponse<AdminDocumentResponse>>(
    `/admin/users/${userId}/documents`,
    { params },
  );
  return data;
}

export interface SearchServiceLanesParams {
  fromState?: string;
  toState?: string;
  truckType?: string;
}

export async function searchServiceLanes(
  params: SearchServiceLanesParams = {},
): Promise<ServiceLaneSearchResponse[]> {
  const { data } = await api.get<ServiceLaneSearchResponse[]>('/service-lanes/search', {
    params,
  });
  return data;
}

export interface SuggestionPageParams extends SearchServiceLanesParams {
  page: number;
  size: number;
}

export async function searchServiceLanesPage(
  params: SuggestionPageParams, signal?: AbortSignal,
): Promise<PagedResponse<ServiceLaneSearchResponse>> {
  const { data } = await api.get<PagedResponse<ServiceLaneSearchResponse>>('/service-lanes/search/page', { params, signal });
  return data;
}

export async function getFleetSuggestionsPage(
  shipmentId: number, params: SuggestionPageParams, signal?: AbortSignal,
): Promise<PagedResponse<FleetSuggestionResponse>> {
  const { data } = await api.get<PagedResponse<FleetSuggestionResponse>>(`/admin/matching/shipments/${shipmentId}/suggestions/page`, { params, signal });
  return data;
}

/** Sends the approved partner load alert and records it (#289, Contract dnm-work#41, rhino-api R34). */
export async function sendPartnerWhatsappAlert(
  shipmentId: number, vendorId: number, clientRequestId: string,
): Promise<WhatsappAlert> {
  const { data } = await api.post<WhatsappAlert>(
    `/admin/notification/shipments/${shipmentId}/partners/${vendorId}/whatsapp`,
    { clientRequestId },
  );
  return data;
}

export interface UpsertServiceLanePayload {
  fromState: string;
  toState: string;
  truckTypes: string[];
}

export interface BatchUpsertServiceLanePayload {
  fromState: string;
  toStates: string[];
  truckTypes: string[];
}

export async function adminUpsertServiceLane(
  fleetManagerId: number,
  payload: UpsertServiceLanePayload,
): Promise<ServiceLaneResponse> {
  const { data } = await api.put<ServiceLaneResponse>(
    `/admin/fleet-managers/${fleetManagerId}/service-lanes`,
    payload,
  );
  return data;
}

export async function adminBatchUpsertServiceLanes(
  fleetManagerId: number,
  payload: BatchUpsertServiceLanePayload,
): Promise<ServiceLaneResponse[]> {
  const { data } = await api.put<ServiceLaneResponse[]>(
    `/admin/fleet-managers/${fleetManagerId}/service-lanes/batch`,
    payload,
  );
  return data;
}

export async function adminDeleteServiceLane(
  fleetManagerId: number,
  laneId: number,
): Promise<void> {
  await api.delete(`/admin/fleet-managers/${fleetManagerId}/service-lanes/${laneId}`);
}

// ─── Automated Matching & Notification Telemetry ──────────────────────────────

export async function getShipmentMatches(
  shipmentId: number,
): Promise<ShipmentVendorMatchResponse[]> {
  const { data } = await api.get<ShipmentVendorMatchResponse[]>(
    `/admin/matching/shipments/${shipmentId}/matches`,
  );
  return data;
}

export async function getNotificationRuns(
  shipmentId: number,
): Promise<NotificationRunResponse[]> {
  const { data } = await api.get<NotificationRunResponse[]>(
    `/admin/notification/shipments/${shipmentId}/runs`,
  );
  return data;
}

export async function getNotificationRunDetails(
  runId: number,
): Promise<NotificationDetailResponse[]> {
  const { data } = await api.get<NotificationDetailResponse[]>(
    `/admin/notification/runs/${runId}/notifications`,
  );
  return data;
}

// ─── Task 10 / Batch P7: On-Demand Notification History ──────────────────────

export async function getShipmentNotificationHistory(
  shipmentId: number,
  filter?: HistoryFilter,
): Promise<NotificationHistoryPage> {
  const { data } = await api.get<NotificationHistoryPage>(
    `/admin/notification/shipments/${shipmentId}/history`,
    { params: filter ?? {} },
  );
  return data;
}

export async function getShipmentNotificationSummary(
  shipmentId: number,
  filter?: Pick<HistoryFilter, 'postingCycle' | 'runId'>,
): Promise<NotificationSummaryResponse> {
  const { data } = await api.get<NotificationSummaryResponse>(
    `/admin/notification/shipments/${shipmentId}/summary`,
    { params: filter ?? {} },
  );
  return data;
}

export async function getNotificationTimeline(
  notificationId: number,
): Promise<NotificationTimelineResponse> {
  const { data } = await api.get<NotificationTimelineResponse>(
    `/admin/notification/notifications/${notificationId}/timeline`,
  );
  return data;
}

export async function getMatchingPolicies(
  name?: string,
): Promise<MatchingPolicyResponse[]> {
  const { data } = await api.get<MatchingPolicyResponse[]>('/admin/matching/policies', {
    params: name ? { name } : undefined,
  });
  return data;
}

export async function getMatchingPolicyById(
  id: number,
): Promise<MatchingPolicyResponse> {
  const { data } = await api.get<MatchingPolicyResponse>(`/admin/matching/policies/${id}`);
  return data;
}

export async function createMatchingPolicyDraft(
  payload: MatchingPolicyDraftRequest,
): Promise<MatchingPolicyResponse> {
  const { data } = await api.post<MatchingPolicyResponse>('/admin/matching/policies', payload);
  return data;
}

export async function activateMatchingPolicy(
  id: number,
): Promise<MatchingPolicyResponse> {
  const { data } = await api.post<MatchingPolicyResponse>(
    `/admin/matching/policies/${id}/activate`,
  );
  return data;
}

export async function getNotificationPolicies(
  name?: string,
): Promise<NotificationPolicyResponse[]> {
  const { data } = await api.get<NotificationPolicyResponse[]>('/admin/notification/policies', {
    params: name ? { name } : undefined,
  });
  return data;
}

export async function getNotificationPolicyById(
  id: number,
): Promise<NotificationPolicyResponse> {
  const { data } = await api.get<NotificationPolicyResponse>(
    `/admin/notification/policies/${id}`,
  );
  return data;
}

export async function createNotificationPolicyDraft(
  payload: NotificationPolicyDraftRequest,
): Promise<NotificationPolicyResponse> {
  const { data } = await api.post<NotificationPolicyResponse>(
    '/admin/notification/policies',
    payload,
  );
  return data;
}

export async function activateNotificationPolicy(
  id: number,
): Promise<NotificationPolicyResponse> {
  const { data } = await api.post<NotificationPolicyResponse>(
    `/admin/notification/policies/${id}/activate`,
  );
  return data;
}

// ─── Phase 7 / Ticket V07/V08: Vendor Interests ──────────────────────────────

export async function getAdminInterests(
  params?: AdminInterestQueueParams,
): Promise<PagedResponse<AdminInterestSummaryResponse>> {
  const { data } = await api.get<PagedResponse<AdminInterestSummaryResponse>>('/admin/interests', {
    params,
  });
  return data;
}

export async function getAdminInterestDetail(
  id: number,
): Promise<AdminInterestDetailResponse> {
  const { data } = await api.get<AdminInterestDetailResponse>(`/admin/interests/${id}`);
  return data;
}

export async function adminInterestAction(
  id: number,
  payload: AdminInterestActionRequest,
  idempotencyKey?: string,
): Promise<AdminInterestDetailResponse> {
  const { data } = await api.post<AdminInterestDetailResponse>(
    `/admin/interests/${id}/actions`,
    payload,
    {
      headers: {
        'Idempotency-Key': idempotencyKey ?? crypto.randomUUID(),
      },
    },
  );
  return data;
}

export async function adminConvertInterestToBid(
  id: number,
  payload: AdminInterestConvertToBidRequest,
  idempotencyKey?: string,
): Promise<AdminInterestDetailResponse> {
  const { data } = await api.post<AdminInterestDetailResponse>(
    `/admin/interests/${id}/convert-to-bid`,
    payload,
    {
      headers: {
        'Idempotency-Key': idempotencyKey ?? crypto.randomUUID(),
      },
    },
  );
  return data;
}



export async function adminUploadProfileDocument(userId: number, file: File, tag: string): Promise<{ fileUrl: string }> {
  const result = await uploadFileDirectToS3({
    file,
    purpose: 'USER_KYC_DOCUMENT',
    targetId: userId,
    documentTag: tag,
  });
  return { fileUrl: result.fileUrl };
}

export async function adminPreviewProfileMedia(userId: number, url: string): Promise<string> {
  const { data } = await api.get<{ url: string }>(`/admin/users/${userId}/media`, { params: { url } });
  return data.url;
}

export interface GetAccountsParams {
  page?: number;
  size?: number;
  search?: string;
}

export async function getAccounts(params: GetAccountsParams = {}): Promise<PagedResponse<AdminAccountResponse>> {
  const { data } = await api.get<PagedResponse<AdminAccountResponse>>('/admin/accounts', { params });
  return data;
}

export async function getAccountDetail(id: number): Promise<AdminAccountDetailResponse> {
  const { data } = await api.get<AdminAccountDetailResponse>(`/admin/accounts/${id}`);
  return data;
}

// ── Operations ───────────────────────────────────────────────────────────────

export interface TriggerExpiryResponse {
  expiredCount: number;
  graceDays: number;
  message: string;
}

export async function triggerShipmentExpiry(): Promise<TriggerExpiryResponse> {
  const { data } = await api.post<TriggerExpiryResponse>('/admin/shipments/trigger-expiry');
  return data;
}

// ─── Shipper billing conditions (#293 Contract) ──────────────────────────────

export async function getShipperBillingConditions(userId: number): Promise<ShipperBillingConditions> {
  const { data } = await api.get<ShipperBillingConditions>(
    `/admin/shippers/${userId}/billing-conditions`,
  );
  return data;
}

export async function putShipperBillingConditions(
  userId: number,
  body: ShipperBillingConditionsRequest,
): Promise<ShipperBillingConditions> {
  const { data } = await api.put<ShipperBillingConditions>(
    `/admin/shippers/${userId}/billing-conditions`,
    body,
  );
  return data;
}

// ─── Shipper rate card and rate quote (#298, Contract dnm-work#155) ──────────
// Demo world only until the build issue wires rhino-api: callers gate on isDemo().

export async function getShipperRateCard(shipperId: number): Promise<RateCardResponse> {
  const { data } = await api.get<RateCardResponse>(`/admin/shippers/${shipperId}/rate-card`);
  return data;
}

export async function getRateQuote(query: RateQuoteQuery): Promise<RateQuoteResponse> {
  const { data } = await api.get<RateQuoteResponse>('/admin/rate-quote', { params: query });
  return data;
}

// ─── Shipper zero calls (#294, Contract dnm-work#150) ─────────────────────────

export async function recordShipmentDelay(
  shipmentId: number,
  body: RecordDelayRequest,
): Promise<ShipmentResponse> {
  const { data } = await api.post<ShipmentResponse>(`/admin/shipments/${shipmentId}/delay`, body);
  return data;
}

export async function getDelayPreview(
  shipmentId: number,
  params: { reason: DelayReason; reasonText?: string | null; newExpectedAt?: string | null },
): Promise<DelayPreviewResponse> {
  const query: Record<string, string> = { reason: params.reason };
  if (params.reasonText) query.reasonText = params.reasonText;
  if (params.newExpectedAt) query.newExpectedAt = params.newExpectedAt;
  const { data } = await api.get<DelayPreviewResponse>(`/admin/shipments/${shipmentId}/delay/preview`, {
    params: query,
  });
  return data;
}

export async function listShipperMessages(shipmentId: number): Promise<ShipperMessage[]> {
  const { data } = await api.get<ShipperMessage[]>(`/admin/shipments/${shipmentId}/shipper-messages`);
  return data;
}

// ─── Approval limits and my approvals (#297 Contract, demo only) ─────────────

export async function getApprovalLimits(): Promise<ApprovalLimits> {
  const { data } = await api.get<ApprovalLimits>('/admin/approval-limits');
  return data;
}

export async function putApprovalLimits(body: ApprovalLimits): Promise<ApprovalLimits> {
  const { data } = await api.put<ApprovalLimits>('/admin/approval-limits', body);
  return data;
}

export async function listMyApprovals(): Promise<MyApprovalRow[]> {
  const { data } = await api.get<MyApprovalRow[]>('/admin/approvals/mine');
  return data;
}
